import axios, { AxiosInstance } from 'axios';
import { format, isValid, parseISO } from 'date-fns';
import { apiConfig } from '@/config/apiConfig';
import { enrichFunnelPercents, fmtAnalyticsCount, fmtCount, fmtDurationSeconds, getVoiceFallbackClient } from './_shared';

// ── Call analytics (/call-analytics) ─────────────────────────────────

export type CallAnalyticsKpiStat = { label: string; val: string; change: number; icon: string };

export type CallAnalyticsTopAgent = {
  name: string;
  sessions: string;
  rate: string;
  icon: string;
  status: string;
  /** Optional second line (e.g. preformatted stats from the API). */
  summary?: string;
};

export type CallAnalyticsFunnelStep = { label: string; value: string; percent: number };

export type CallAnalyticsCampaignTableRow = {
  id: string;
  name: string;
  icon: string;
  status: string;
  scans: string;
  sessions: string;
  completed: string;
  completedRate: string;
  booked: string;
  rate: string;
  avgDuration: string;
  avgSentiment: string;
  rev: string;
  cpa: string;
  color: 'success.main' | 'warning.main' | 'error.main';
};

export type CallAnalyticsAgentTableRow = {
  name: string;
  icon: string;
  sessions: string;
  rate: string;
  sent: string;
  esc: string;
  cpa: string;
};

export type CallAnalyticsCostRow = { name: string; vol: string; rate: string; total: string };

export type CallAnalyticsActivityChart = {
  dailyLabels: string[];
  dailyTotals: number[];
  weeklyLabels: string[];
  weeklyTotals: number[];
  /** Apex series name (legend / tooltip). */
  seriesName?: string;
};

/** Copy for Call Analytics — optional overrides merged with `DEFAULT_CALL_ANALYTICS_UI` in the UI (see `mergeCallAnalyticsUi`).
 * Backend may send snake_case equivalents (see `coerceAnalyticsUi`). */
export type CallAnalyticsUi = {
  pageTitle?: string;
  pageSubtitle?: string;
  /** One label per primary nav tab (Overview → Session log). */
  navTabs?: string[];
  fullReportButton?: string;
  exportCsvButton?: string;
  exportingCsv?: string;
  sessionActivityTitle?: string;
  rangeDaily?: string;
  rangeWeekly?: string;
  topAgentsTitle?: string;
  viewAllAgents?: string;
  funnelConversionTitle?: string;
  costIntelligenceTitle?: string;
  avgSessionCostLabel?: string;
  avgOutcomeCostLabel?: string;
  totalSpendLabel?: string;
  /** Full sentence shown in the savings callout (replaces templated copy). */
  savingsNote?: string;
  projectPerformanceTitle?: string;
  campaignScopeBadge?: string;
  campaignTableColumns?: string[];
  agentTableColumns?: string[];
  costBilledTitle?: string;
  costTableColumns?: string[];
  platformAdjustedTotalLabel?: string;
  conversionFunnelTitle?: string;
  funnelEmptyMessage?: string;
  objectionsTitle?: string;
  objectionsTableColumns?: string[];
  objectionsEmptyMessage?: string;
  intentsEmptyMessage?: string;
  sessionExportButton?: string;
  sessionExporting?: string;
  sessionTableColumns?: string[];
  sessionLogEmptyMessage?: string;
  /** When session activity bars have no data for the chosen range */
  sessionActivityEmptyMessage?: string;
  overviewMetricsEmptyMessage?: string;
  topAgentsEmptyMessage?: string;
  overviewFunnelEmptyMessage?: string;
  campaignTableEmptyMessage?: string;
  agentTableEmptyMessage?: string;
  costTableEmptyMessage?: string;
  /** Full-page / chart area when `/analytics/full` fails */
  analyticsFetchErrorMessage?: string;
  dialogTitle?: string;
  dialogSubtitleAllSessions?: string;
  dialogExportPdf?: string;
  dialogNavTabs?: string[];
  dialogCostBreakdownTitle?: string;
  dialogPlatformTotalLabel?: string;
  dialogFunnelEmpty?: string;
  dialogSignalsEmpty?: string;
  dialogSessionsEmpty?: string;
  sessionsTabTitle?: string;
  channelMixTitle?: string;
  outcomesTitle?: string;
  signalsTabTitle?: string;
  totalSignalsLabel?: string;
  highIntentSignalsLabel?: string;
  avgSentimentLabel?: string;
  objectionResolveRateLabel?: string;
  topIntentsTitle?: string;
  topObjectionsTitle?: string;
  sentimentTrajectoryTitle?: string;
  signalTypeBreakdownTitle?: string;
  attributionTabTitle?: string;
  costsTabTitle?: string;
  tokensGeneratedLabel?: string;
  tokensScannedLabel?: string;
  creativesTrackedLabel?: string;
  perCreativeAttributionTitle?: string;
  spend30DayLabel?: string;
  creditsUsedLabel?: string;
  sessionsBilledLabel?: string;
  costPerSessionLabel?: string;
  costPerOutcomeLabel?: string;
  costPerBookingLabel?: string;
  costBreakdownTitle?: string;
  unitPricingTitle?: string;
  // KPI icons — Signals tab
  totalSignalsIcon?: string;
  highIntentSignalsIcon?: string;
  avgSentimentIcon?: string;
  objectionResolveRateIcon?: string;
  // KPI icons — Attribution tab
  tokensGeneratedIcon?: string;
  tokensScannedIcon?: string;
  creativesTrackedIcon?: string;
  // KPI icons — Costs tab
  spend30DayIcon?: string;
  creditsUsedIcon?: string;
  sessionsBilledIcon?: string;
  costPerSessionIcon?: string;
  costPerOutcomeIcon?: string;
  costPerBookingIcon?: string;
};

export type CallAnalyticsPayload = {
  overview?: {
    total_sessions?: number;
    avg_sentiment?: number;
    completion_rate?: string | number;
    completed_sessions?: number | string;
  };
  overviewKpis: CallAnalyticsKpiStat[];
  topAgents: CallAnalyticsTopAgent[];
  funnelSteps: CallAnalyticsFunnelStep[];
  costSummary: { avgSessionCost: string; avgOutcomeCost: string; totalSpend: string; savingsAmount: string };
  campaignRows: CallAnalyticsCampaignTableRow[];
  agentTabKpis: CallAnalyticsKpiStat[];
  agentRows: CallAnalyticsAgentTableRow[];
  costTabKpis: CallAnalyticsKpiStat[];
  costRows: CallAnalyticsCostRow[];
  platformAdjustedTotal: string;
  activityChart: CallAnalyticsActivityChart;
  channelData: { label: string; value: number }[];
  outcomeData: { label: string; value: number }[];
  ui?: CallAnalyticsUi;
};

function createEmptyCallAnalyticsPayload(): CallAnalyticsPayload {
  return {
    overview: undefined,
    overviewKpis: [],
    topAgents: [],
    funnelSteps: [],
    costSummary: { avgSessionCost: '', avgOutcomeCost: '', totalSpend: '', savingsAmount: '' },
    campaignRows: [],
    agentTabKpis: [],
    agentRows: [],
    costTabKpis: [],
    costRows: [],
    platformAdjustedTotal: '',
    activityChart: {
      dailyLabels: [],
      dailyTotals: [],
      weeklyLabels: [],
      weeklyTotals: [],
    },
    channelData: [],
    outcomeData: [],
    ui: undefined,
  };
}

function mapCallAnalyticsKpiStat(x: any): CallAnalyticsKpiStat {
  return {
    label: String(x.label ?? ''),
    val: String(x.val ?? x.value ?? ''),
    change: Number(x.change ?? x.change_pct ?? x.delta_pct ?? 0),
    icon: String(x.icon ?? ''),
  };
}

function mapCallAnalyticsCostRow(r: any): CallAnalyticsCostRow {
  return {
    name: String(r.name ?? r.component ?? ''),
    vol: String(r.vol ?? r.volume ?? ''),
    rate: String(r.rate ?? ''),
    total: String(r.total ?? ''),
  };
}

/** Accept `ui`, `labels`, snake_case keys, or camelCase on the same object. */
export function coerceAnalyticsUi(raw: unknown): CallAnalyticsUi | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const u = raw as Record<string, unknown>;
  const s = (a: unknown, b: unknown) => {
    const v = a ?? b;
    if (v == null) return undefined;
    const t = String(v).trim();
    return t === '' ? undefined : t;
  };
  const arr = (a: unknown, b: unknown) => {
    const v = (a ?? b) as unknown;
    if (!Array.isArray(v)) return undefined;
    const out = v.map((x) => String(x)).filter((x) => x.trim() !== '');
    return out.length ? out : undefined;
  };
  const out: CallAnalyticsUi = {
    pageSubtitle: s(u.page_subtitle, u.pageSubtitle),
    navTabs: arr(u.nav_tabs, u.navTabs),
    fullReportButton: s(u.full_report_button, u.fullReportButton),
    exportCsvButton: s(u.export_csv_button, u.exportCsvButton),
    exportingCsv: s(u.exporting_csv, u.exportingCsv),
    sessionActivityTitle: s(u.session_activity_title, u.sessionActivityTitle),
    rangeDaily: s(u.range_daily, u.rangeDaily),
    rangeWeekly: s(u.range_weekly, u.rangeWeekly),
    topAgentsTitle: s(u.top_agents_title, u.topAgentsTitle),
    viewAllAgents: s(u.view_all_agents, u.viewAllAgents),
    funnelConversionTitle: s(u.funnel_conversion_title, u.funnelConversionTitle),
    costIntelligenceTitle: s(u.cost_intelligence_title, u.costIntelligenceTitle),
    avgSessionCostLabel: s(u.avg_session_cost_label, u.avgSessionCostLabel),
    avgOutcomeCostLabel: s(u.avg_outcome_cost_label, u.avgOutcomeCostLabel),
    totalSpendLabel: s(u.total_spend_label, u.totalSpendLabel),
    savingsNote: s(u.savings_note, u.savingsNote),
    projectPerformanceTitle: s(u.project_performance_title, u.projectPerformanceTitle),
    campaignScopeBadge: s(u.campaign_scope_badge, u.campaignScopeBadge),
    campaignTableColumns: arr(u.campaign_table_columns, u.campaignTableColumns),
    agentTableColumns: arr(u.agent_table_columns, u.agentTableColumns),
    costBilledTitle: s(u.cost_billed_title, u.costBilledTitle),
    costTableColumns: arr(u.cost_table_columns, u.costTableColumns),
    platformAdjustedTotalLabel: s(u.platform_adjusted_total_label, u.platformAdjustedTotalLabel),
    conversionFunnelTitle: s(u.conversion_funnel_title, u.conversionFunnelTitle),
    funnelEmptyMessage: s(u.funnel_empty_message, u.funnelEmptyMessage),
    objectionsTitle: s(u.objections_title, u.objectionsTitle),
    objectionsTableColumns: arr(u.objections_table_columns, u.objectionsTableColumns),
    objectionsEmptyMessage: s(u.objections_empty_message, u.objectionsEmptyMessage),
    sessionExportButton: s(u.session_export_button, u.sessionExportButton),
    sessionExporting: s(u.session_exporting, u.sessionExporting),
    sessionTableColumns: arr(u.session_table_columns, u.sessionTableColumns),
    sessionLogEmptyMessage: s(u.session_log_empty_message, u.sessionLogEmptyMessage),
    sessionActivityEmptyMessage: s(u.session_activity_empty_message, u.sessionActivityEmptyMessage),
    overviewMetricsEmptyMessage: s(u.overview_metrics_empty_message, u.overviewMetricsEmptyMessage),
    topAgentsEmptyMessage: s(u.top_agents_empty_message, u.topAgentsEmptyMessage),
    overviewFunnelEmptyMessage: s(u.overview_funnel_empty_message, u.overviewFunnelEmptyMessage),
    campaignTableEmptyMessage: s(u.campaign_table_empty_message, u.campaignTableEmptyMessage),
    agentTableEmptyMessage: s(u.agent_table_empty_message, u.agentTableEmptyMessage),
    costTableEmptyMessage: s(u.cost_table_empty_message, u.costTableEmptyMessage),
    analyticsFetchErrorMessage: s(u.analytics_fetch_error_message, u.analyticsFetchErrorMessage),
    dialogTitle: s(u.dialog_title, u.dialogTitle),
    dialogSubtitleAllSessions: s(u.dialog_subtitle_all_sessions, u.dialogSubtitleAllSessions),
    dialogExportPdf: s(u.dialog_export_pdf, u.dialogExportPdf),
    dialogNavTabs: arr(u.dialog_nav_tabs, u.dialogNavTabs),
    dialogCostBreakdownTitle: s(u.dialog_cost_breakdown_title, u.dialogCostBreakdownTitle),
    dialogPlatformTotalLabel: s(u.dialog_platform_total_label, u.dialogPlatformTotalLabel),
    dialogFunnelEmpty: s(u.dialog_funnel_empty, u.dialogFunnelEmpty),
    dialogSignalsEmpty: s(u.dialog_signals_empty, u.dialogSignalsEmpty),
    dialogSessionsEmpty: s(u.dialog_sessions_empty, u.dialogSessionsEmpty),
    sessionsTabTitle: s(u.sessions_tab_title, u.sessionsTabTitle),
    channelMixTitle: s(u.channel_mix_title, u.channelMixTitle),
    outcomesTitle: s(u.outcomes_title, u.outcomesTitle),
    signalsTabTitle: s(u.signals_tab_title, u.signalsTabTitle),
    totalSignalsLabel: s(u.total_signals_label, u.totalSignalsLabel),
    highIntentSignalsLabel: s(u.high_intent_signals_label, u.highIntentSignalsLabel),
    avgSentimentLabel: s(u.avg_sentiment_label, u.avgSentimentLabel),
    objectionResolveRateLabel: s(u.objection_resolve_rate_label, u.objectionResolveRateLabel),
    topIntentsTitle: s(u.top_intents_title, u.topIntentsTitle),
    topObjectionsTitle: s(u.top_objections_title, u.topObjectionsTitle),
    sentimentTrajectoryTitle: s(u.sentiment_trajectory_title, u.sentimentTrajectoryTitle),
    signalTypeBreakdownTitle: s(u.signal_type_breakdown_title, u.signalTypeBreakdownTitle),
    attributionTabTitle: s(u.attribution_tab_title, u.attributionTabTitle),
    costsTabTitle: s(u.costs_tab_title, u.costsTabTitle),
    tokensGeneratedLabel: s(u.tokens_generated_label, u.tokensGeneratedLabel),
    tokensScannedLabel: s(u.tokens_scanned_label, u.tokensScannedLabel),
    creativesTrackedLabel: s(u.creatives_tracked_label, u.creativesTrackedLabel),
    perCreativeAttributionTitle: s(u.per_creative_attribution_title, u.perCreativeAttributionTitle),
    spend30DayLabel: s(u.spend_30_day_label, u.spend30DayLabel),
    creditsUsedLabel: s(u.credits_used_label, u.creditsUsedLabel),
    sessionsBilledLabel: s(u.sessions_billed_label, u.sessionsBilledLabel),
    costPerSessionLabel: s(u.cost_per_session_label, u.costPerSessionLabel),
    costPerOutcomeLabel: s(u.cost_per_outcome_label, u.costPerOutcomeLabel),
    costPerBookingLabel: s(u.cost_per_booking_label, u.costPerBookingLabel),
    costBreakdownTitle: s(u.cost_breakdown_title, u.costBreakdownTitle),
    unitPricingTitle: s(u.unit_pricing_title, u.unitPricingTitle),
  };
  const hasAny = Object.values(out).some((v) => v !== undefined);
  return hasAny ? out : undefined;
}

/** English defaults when `/analytics/full` omits `ui` (staging often has no `ui` block). */
export const DEFAULT_CALL_ANALYTICS_UI: Required<CallAnalyticsUi> = {
  pageTitle: 'Voice Analytics',
  pageSubtitle: '',
  navTabs: [
    'Overview',
    'Sessions',
    'By Campaign',
    'By Agent',
    'Signals',
    'Attribution',
    'Costs',
    'A/B Voice',
    'SMS',
    'Post-Call',
    'Reactivation',
  ],
  fullReportButton: 'Full Report',
  exportCsvButton: 'Export',
  exportingCsv: 'Exporting...',
  sessionActivityTitle: 'Session Activity',
  rangeDaily: 'Daily',
  rangeWeekly: 'Weekly',
  topAgentsTitle: 'Top Performing Agents',
  viewAllAgents: 'View all agents',
  funnelConversionTitle: 'Funnel Conversion',
  costIntelligenceTitle: 'Cost Intelligence',
  avgSessionCostLabel: 'Avg. Session Cost',
  avgOutcomeCostLabel: 'Avg. Outcome Cost',
  totalSpendLabel: 'Total Spend',
  savingsNote: '',
  projectPerformanceTitle: 'Project Performance',
  campaignScopeBadge: 'Active All-time',
  campaignTableColumns: ['Campaign', 'Scans', 'Sessions', 'Book Rate', 'Revenue', 'CPA'],
  agentTableColumns: ['Agent', 'Sessions', 'Book Rate', 'Sentiment', 'Escalations', 'CPA'],
  costBilledTitle: 'Billed Components',
  costTableColumns: ['Component', 'Volume', 'Rate', 'Total'],
  platformAdjustedTotalLabel: 'Platform Adjusted Total',
  conversionFunnelTitle: 'Conversion Funnel',
  funnelEmptyMessage:
    'There is no funnel data for this scope yet. Stages typically fill in after voice sessions progress through dialing, qualification, bookings, or other milestones your workspace tracks.',
  objectionsTitle: 'Conversation Signals',
  objectionsTableColumns: ['Signal Type', 'Count', 'Share'],
  objectionsEmptyMessage:
    'There are no conversation signals to analyze yet. This table summarizes intents, objections, and similar labels parsed from transcripts—data appears after callers complete sessions.',
  intentsEmptyMessage:
    'No intents detected yet. Headline intents like inquiries, bookings, or support requests are extracted automatically once voice sessions are processed.',
  sessionExportButton: 'Export Sessions',
  sessionExporting: 'Exporting...',
  sessionTableColumns: ['Session', 'Time', 'Contact', 'Agent', 'Campaign', 'Outcome', 'Duration'],
  sessionLogEmptyMessage:
    'No voice sessions to list yet. Rows appear when callers interact with active voice campaigns; check other tabs for funnel or campaigns if you expected activity sooner.',
  sessionActivityEmptyMessage:
    'Session volume isn’t charted yet for Daily or Weekly. Bars display once callers complete sessions in your voice programs so counts can aggregate by day or week.',
  overviewMetricsEmptyMessage:
    'Summary KPI cards are unavailable because headline metrics weren’t returned for your workspace yet. KPIs populate when session and booking aggregates exist for this tenant.',
  topAgentsEmptyMessage:
    'No agent leaderboard yet. Agents are ranked here after assigned voice assistants receive sessions so performance (sessions, book rate, status) can be compared.',
  overviewFunnelEmptyMessage:
    'Funnel checkpoints aren’t summarized on the overview card yet—either no sessions qualify for these steps or summaries haven’t synced. Detailed funnel data may still appear on the Funnel tab when available.',
  campaignTableEmptyMessage:
    'Campaign performance hasn’t synced for this tenant yet. Rows show once voice campaigns have sessions or scans attributed; confirm campaigns exist and callers have completed conversations.',
  agentTableEmptyMessage:
    'No per-agent table rows yet—either no assistants have routed sessions here or aggregates haven’t synced. KPIs above also stay empty until agent-level summaries exist.',
  costTableEmptyMessage:
    'No billed component lines yet—platform fees, usage, or bookings may not have posted for this period. Totals and line items appear when billing data is present for your account.',
  analyticsFetchErrorMessage:
    'Voice analytics could not be loaded. Check your network, sign in again if your session expired, and confirm you have access to this workspace. Retry in a few minutes or contact support if it persists.',
  dialogTitle: 'Full Performance Report',
  dialogSubtitleAllSessions: 'All Voice Sessions',
  dialogExportPdf: 'Export PDF',
  dialogNavTabs: ['Summary', 'Conversion Funnel', 'Objections', 'Cost Analysis', 'Session Log'],
  dialogCostBreakdownTitle: 'Cost Breakdown',
  dialogPlatformTotalLabel: 'Platform Adjusted Total',
  dialogFunnelEmpty:
    'No funnel section to render—data has not been returned yet for this workspace. Try again after campaigns record sessions.',
  dialogSignalsEmpty:
    'No objection or signal rollup is available yet. Exported PDF summaries will populate after conversation analytics ingest labels from sessions.',
  dialogSessionsEmpty:
    'No sessions are included for this preview—either none exist or filtered results are empty. Sessions must be loaded before this section can list them.',
  sessionsTabTitle: 'Sessions',
  channelMixTitle: 'Channel Mix',
  outcomesTitle: 'Outcomes',
  signalsTabTitle: 'Signals',
  totalSignalsLabel: 'Total Signals',
  highIntentSignalsLabel: 'High Intent Signals',
  avgSentimentLabel: 'Avg. Sentiment',
  objectionResolveRateLabel: 'Objection Resolve Rate',
  topIntentsTitle: 'Top Intents Detected',
  topObjectionsTitle: 'Top Objections',
  sentimentTrajectoryTitle: 'Sentiment Trajectory',
  signalTypeBreakdownTitle: 'Signal Type Breakdown',
  attributionTabTitle: 'Attribution',
  costsTabTitle: 'Costs',
  tokensGeneratedLabel: 'Tokens Generated',
  tokensScannedLabel: 'Tokens Scanned',
  creativesTrackedLabel: 'Creatives Tracked',
  perCreativeAttributionTitle: 'Per-Creative Attribution',
  spend30DayLabel: '30-Day Spend',
  creditsUsedLabel: 'Credits Used',
  sessionsBilledLabel: 'Sessions Billed',
  costPerSessionLabel: 'Cost / Session',
  costPerOutcomeLabel: 'Cost / Outcome',
  costPerBookingLabel: 'Cost / Booking',
  costBreakdownTitle: 'Cost Breakdown by Event Type',
  unitPricingTitle: 'Unit Pricing Reference',
  totalSignalsIcon: 'solar:target-bold',
  highIntentSignalsIcon: 'solar:fire-bold',
  avgSentimentIcon: 'solar:heart-bold',
  objectionResolveRateIcon: 'solar:shield-check-bold',
  tokensGeneratedIcon: 'solar:qr-code-bold',
  tokensScannedIcon: 'solar:scanner-bold',
  creativesTrackedIcon: 'solar:clapperboard-edit-bold',
  spend30DayIcon: 'solar:wad-of-money-bold',
  creditsUsedIcon: 'solar:database-bold',
  sessionsBilledIcon: 'solar:bill-list-bold',
  costPerSessionIcon: 'solar:user-rounded-bold',
  costPerOutcomeIcon: 'solar:check-circle-bold',
  costPerBookingIcon: 'solar:calendar-bold',
};

function pickTabLabels(api: string[] | undefined, fallback: readonly string[]): string[] {
  if (!api?.length) return [...fallback];
  return fallback.map((fb, i) => {
    const v = api[i];
    return typeof v === 'string' && v.trim() !== '' ? v : fb;
  });
}

function pickTableColumns(api: string[] | undefined, fallback: readonly string[]): string[] {
  if (Array.isArray(api) && api.length > 0) return api.map((x) => String(x));
  return [...fallback];
}

/** Resolved UI for Call Analytics — API overrides win per-field when non-empty / non-empty array. */
export function mergeCallAnalyticsUi(api?: CallAnalyticsUi | null): Required<CallAnalyticsUi> {
  const d = DEFAULT_CALL_ANALYTICS_UI;
  if (!api) return { ...d };
  return {
    pageTitle: api.pageTitle ?? d.pageTitle,
    pageSubtitle: api.pageSubtitle ?? d.pageSubtitle,
    navTabs: pickTabLabels(api.navTabs, d.navTabs),
    fullReportButton: api.fullReportButton ?? d.fullReportButton,
    exportCsvButton: api.exportCsvButton ?? d.exportCsvButton,
    exportingCsv: api.exportingCsv ?? d.exportingCsv,
    sessionActivityTitle: api.sessionActivityTitle ?? d.sessionActivityTitle,
    rangeDaily: api.rangeDaily ?? d.rangeDaily,
    rangeWeekly: api.rangeWeekly ?? d.rangeWeekly,
    topAgentsTitle: api.topAgentsTitle ?? d.topAgentsTitle,
    viewAllAgents: api.viewAllAgents ?? d.viewAllAgents,
    funnelConversionTitle: api.funnelConversionTitle ?? d.funnelConversionTitle,
    costIntelligenceTitle: api.costIntelligenceTitle ?? d.costIntelligenceTitle,
    avgSessionCostLabel: api.avgSessionCostLabel ?? d.avgSessionCostLabel,
    avgOutcomeCostLabel: api.avgOutcomeCostLabel ?? d.avgOutcomeCostLabel,
    totalSpendLabel: api.totalSpendLabel ?? d.totalSpendLabel,
    savingsNote: api.savingsNote ?? d.savingsNote,
    projectPerformanceTitle: api.projectPerformanceTitle ?? d.projectPerformanceTitle,
    campaignScopeBadge: api.campaignScopeBadge ?? d.campaignScopeBadge,
    campaignTableColumns: pickTableColumns(api.campaignTableColumns, d.campaignTableColumns),
    agentTableColumns: pickTableColumns(api.agentTableColumns, d.agentTableColumns),
    costBilledTitle: api.costBilledTitle ?? d.costBilledTitle,
    costTableColumns: pickTableColumns(api.costTableColumns, d.costTableColumns),
    platformAdjustedTotalLabel: api.platformAdjustedTotalLabel ?? d.platformAdjustedTotalLabel,
    conversionFunnelTitle: api.conversionFunnelTitle ?? d.conversionFunnelTitle,
    funnelEmptyMessage: api.funnelEmptyMessage ?? d.funnelEmptyMessage,
    objectionsTitle: api.objectionsTitle ?? d.objectionsTitle,
    objectionsTableColumns: pickTableColumns(api.objectionsTableColumns, d.objectionsTableColumns),
    objectionsEmptyMessage: api.objectionsEmptyMessage ?? d.objectionsEmptyMessage,
    intentsEmptyMessage: api.intentsEmptyMessage ?? d.intentsEmptyMessage,
    sessionExportButton: api.sessionExportButton ?? d.sessionExportButton,
    sessionExporting: api.sessionExporting ?? d.sessionExporting,
    sessionTableColumns: pickTableColumns(api.sessionTableColumns, d.sessionTableColumns),
    sessionLogEmptyMessage: api.sessionLogEmptyMessage ?? d.sessionLogEmptyMessage,
    sessionActivityEmptyMessage: api.sessionActivityEmptyMessage ?? d.sessionActivityEmptyMessage,
    overviewMetricsEmptyMessage: api.overviewMetricsEmptyMessage ?? d.overviewMetricsEmptyMessage,
    topAgentsEmptyMessage: api.topAgentsEmptyMessage ?? d.topAgentsEmptyMessage,
    overviewFunnelEmptyMessage: api.overviewFunnelEmptyMessage ?? d.overviewFunnelEmptyMessage,
    campaignTableEmptyMessage: api.campaignTableEmptyMessage ?? d.campaignTableEmptyMessage,
    agentTableEmptyMessage: api.agentTableEmptyMessage ?? d.agentTableEmptyMessage,
    costTableEmptyMessage: api.costTableEmptyMessage ?? d.costTableEmptyMessage,
    analyticsFetchErrorMessage: api.analyticsFetchErrorMessage ?? d.analyticsFetchErrorMessage,
    sessionsTabTitle: api.sessionsTabTitle ?? d.sessionsTabTitle,
    channelMixTitle: api.channelMixTitle ?? d.channelMixTitle,
    outcomesTitle: api.outcomesTitle ?? d.outcomesTitle,
    signalsTabTitle: api.signalsTabTitle ?? d.signalsTabTitle,
    totalSignalsLabel: api.totalSignalsLabel ?? d.totalSignalsLabel,
    highIntentSignalsLabel: api.highIntentSignalsLabel ?? d.highIntentSignalsLabel,
    avgSentimentLabel: api.avgSentimentLabel ?? d.avgSentimentLabel,
    objectionResolveRateLabel: api.objectionResolveRateLabel ?? d.objectionResolveRateLabel,
    topIntentsTitle: api.topIntentsTitle ?? d.topIntentsTitle,
    topObjectionsTitle: api.topObjectionsTitle ?? d.topObjectionsTitle,
    sentimentTrajectoryTitle: api.sentimentTrajectoryTitle ?? d.sentimentTrajectoryTitle,
    signalTypeBreakdownTitle: api.signalTypeBreakdownTitle ?? d.signalTypeBreakdownTitle,
    attributionTabTitle: api.attributionTabTitle ?? d.attributionTabTitle,
    costsTabTitle: api.costsTabTitle ?? d.costsTabTitle,
    tokensGeneratedLabel: api.tokensGeneratedLabel ?? d.tokensGeneratedLabel,
    tokensScannedLabel: api.tokensScannedLabel ?? d.tokensScannedLabel,
    creativesTrackedLabel: api.creativesTrackedLabel ?? d.creativesTrackedLabel,
    perCreativeAttributionTitle: api.perCreativeAttributionTitle ?? d.perCreativeAttributionTitle,
    spend30DayLabel: api.spend30DayLabel ?? d.spend30DayLabel,
    creditsUsedLabel: api.creditsUsedLabel ?? d.creditsUsedLabel,
    sessionsBilledLabel: api.sessionsBilledLabel ?? d.sessionsBilledLabel,
    costPerSessionLabel: api.costPerSessionLabel ?? d.costPerSessionLabel,
    costPerOutcomeLabel: api.costPerOutcomeLabel ?? d.costPerOutcomeLabel,
    costPerBookingLabel: api.costPerBookingLabel ?? d.costPerBookingLabel,
    costBreakdownTitle: api.costBreakdownTitle ?? d.costBreakdownTitle,
    unitPricingTitle: api.unitPricingTitle ?? d.unitPricingTitle,
    totalSignalsIcon: api.totalSignalsIcon ?? d.totalSignalsIcon,
    highIntentSignalsIcon: api.highIntentSignalsIcon ?? d.highIntentSignalsIcon,
    avgSentimentIcon: api.avgSentimentIcon ?? d.avgSentimentIcon,
    objectionResolveRateIcon: api.objectionResolveRateIcon ?? d.objectionResolveRateIcon,
    tokensGeneratedIcon: api.tokensGeneratedIcon ?? d.tokensGeneratedIcon,
    tokensScannedIcon: api.tokensScannedIcon ?? d.tokensScannedIcon,
    creativesTrackedIcon: api.creativesTrackedIcon ?? d.creativesTrackedIcon,
    spend30DayIcon: api.spend30DayIcon ?? d.spend30DayIcon,
    creditsUsedIcon: api.creditsUsedIcon ?? d.creditsUsedIcon,
    sessionsBilledIcon: api.sessionsBilledIcon ?? d.sessionsBilledIcon,
    costPerSessionIcon: api.costPerSessionIcon ?? d.costPerSessionIcon,
    costPerOutcomeIcon: api.costPerOutcomeIcon ?? d.costPerOutcomeIcon,
    costPerBookingIcon: api.costPerBookingIcon ?? d.costPerBookingIcon,
    dialogTitle: api.dialogTitle ?? d.dialogTitle,
    dialogSubtitleAllSessions: api.dialogSubtitleAllSessions ?? d.dialogSubtitleAllSessions,
    dialogExportPdf: api.dialogExportPdf ?? d.dialogExportPdf,
    dialogNavTabs: pickTabLabels(api.dialogNavTabs, d.dialogNavTabs),
    dialogCostBreakdownTitle: api.dialogCostBreakdownTitle ?? d.dialogCostBreakdownTitle,
    dialogPlatformTotalLabel: api.dialogPlatformTotalLabel ?? d.dialogPlatformTotalLabel,
    dialogFunnelEmpty: api.dialogFunnelEmpty ?? d.dialogFunnelEmpty,
    dialogSignalsEmpty: api.dialogSignalsEmpty ?? d.dialogSignalsEmpty,
    dialogSessionsEmpty: api.dialogSessionsEmpty ?? d.dialogSessionsEmpty,
  };
}

/** Build KPI cards when the API sends a metrics object under `overview` instead of `overview_kpis`. */
/** Max KPI cards on Call Analytics overview — filled in priority order when values exist. */
const OVERVIEW_KPI_CARD_LIMIT = 8;

function overviewMetricsToKpis(ov: Record<string, unknown>): CallAnalyticsKpiStat[] {
  const rows: CallAnalyticsKpiStat[] = [];
  const add = (label: string, val: unknown, icon: string, format: 'count' | 'percent' | 'duration' | 'sentiment' | 'cents' | 'raw' = 'count') => {
    if (rows.length >= OVERVIEW_KPI_CARD_LIMIT) return;
    if (val == null || val === '') return;
    let s: string;
    if (format === 'percent') {
      const n = Number(val);
      if (Number.isNaN(n)) return;
      s = `${n}%`;
    } else if (format === 'duration') {
      const n = Number(val);
      if (Number.isNaN(n)) return;
      s = fmtDurationSeconds(n);
    } else if (format === 'sentiment') {
      const n = Number(val);
      if (Number.isNaN(n)) return;
      s = n.toFixed(2);
    } else if (format === 'cents') {
      const n = Number(val);
      if (Number.isNaN(n)) return;
      s = `$${(n / 100).toFixed(2)}`;
    } else if (format === 'raw') {
      s = String(val);
    } else {
      s = fmtAnalyticsCount(val);
    }
    rows.push({ label, val: s, change: 0, icon });
  };
  // Priority: volume → funnel health → outcomes depth → experience (fallbacks fill remaining slots)
  add('Sessions', ov.total_sessions, 'solar:phone-calling-bold');
  add('Booked', ov.total_booked ?? ov.total_appointments, 'solar:calendar-bold');
  add('Qualified', ov.qualified, 'solar:star-circle-bold');
  add('Contacts captured', ov.total_contacts ?? ov.contacts, 'solar:users-group-rounded-bold');
  add('Appointments', ov.total_appointments, 'solar:calendar-date-bold');
  add('Avg duration', ov.avg_duration_seconds, 'solar:clock-circle-bold', 'duration');
  add('Cost / outcome', ov.per_outcome_cents, 'solar:wad-of-money-bold', 'cents');
  add('Avg. sentiment', ov.avg_sentiment, 'solar:emoji-funny-circle-bold', 'sentiment');
  return rows;
}

/** Map `/api/analytics/full` (any shape) into `CallAnalyticsPayload` for the UI. Missing keys stay empty — no fixture merge. */
export function normalizeCallAnalyticsFull(data: unknown): CallAnalyticsPayload {
  const out = createEmptyCallAnalyticsPayload();
  const d = data as any;
  if (!d || typeof d !== 'object') return out;

  const overviewBlock =
    d.overview && typeof d.overview === 'object' && !Array.isArray(d.overview)
      ? (d.overview as Record<string, unknown>)
      : d.total_sessions != null || d.overview_kpis || d.overviewKpis
      ? (d as Record<string, unknown>)
      : null;

  const overviewSrc = d.overview_kpis ?? d.overviewKpis;
  if (Array.isArray(overviewSrc)) {
    out.overviewKpis = overviewSrc.map(mapCallAnalyticsKpiStat).slice(0, OVERVIEW_KPI_CARD_LIMIT);
  } else if (overviewBlock && Object.keys(overviewBlock).length > 0) {
    out.overviewKpis = overviewMetricsToKpis(overviewBlock);
  }

  if (overviewBlock) {
    out.overview = {
      total_sessions: overviewBlock.total_sessions != null ? Number(overviewBlock.total_sessions) : undefined,
      avg_sentiment: overviewBlock.avg_sentiment != null ? Number(overviewBlock.avg_sentiment) : undefined,
      completion_rate: overviewBlock.completion_rate as string | number | undefined,
      completed_sessions: overviewBlock.completed_sessions as number | string | undefined,
    };
  }

  const topSrc = d.top_agents ?? d.topAgents;
  if (Array.isArray(topSrc)) {
    out.topAgents = topSrc.map((a: any) => {
      const summary = String(a.summary ?? a.subtitle ?? a.detail ?? '').trim();
      return {
        name: String(a.name ?? ''),
        sessions: fmtAnalyticsCount(a.sessions ?? a.session_count),
        rate: String(a.rate ?? a.book_rate ?? ''),
        icon: String(a.icon ?? ''),
        status: String(a.status ?? ''),
        ...(summary ? { summary } : {}),
      };
    });
  }

  let funnelSrc = d.funnel_steps ?? d.funnelSteps;
  if (!Array.isArray(funnelSrc) && d.funnel != null) {
    if (Array.isArray(d.funnel)) funnelSrc = d.funnel;
    else if (typeof d.funnel === 'object' && Array.isArray(d.funnel.funnel)) funnelSrc = d.funnel.funnel;
  }
  if (Array.isArray(funnelSrc)) {
    out.funnelSteps = enrichFunnelPercents(
      funnelSrc.map((s: any) => ({
        label: String(s.label ?? s.stage ?? s.step ?? ''),
        value: fmtAnalyticsCount(s.value ?? s.count),
        percent: Number(s.percent ?? s.pct ?? 0),
      })),
    );
  }

  const costSrc = d.cost_summary ?? d.costSummary;
  if (costSrc && typeof costSrc === 'object') {
    const c = costSrc as any;
    out.costSummary = {
      avgSessionCost: String(c.avg_session_cost ?? c.avgSessionCost ?? ''),
      avgOutcomeCost: String(c.avg_outcome_cost ?? c.avgOutcomeCost ?? ''),
      totalSpend: String(c.total_spend ?? c.totalSpend ?? ''),
      savingsAmount: String(c.savings ?? c.savingsAmount ?? ''),
    };
  }
  if (
    overviewBlock &&
    (!out.costSummary.totalSpend?.trim() || !out.costSummary.avgSessionCost?.trim()) &&
    overviewBlock.cost &&
    typeof overviewBlock.cost === 'object'
  ) {
    const c = overviewBlock.cost as Record<string, unknown>;
    const totalCents = c.total_cents ?? c.totalCents;
    const perSessionCents = c.per_session_cents ?? c.perSessionCents;
    const totalDollars = c.total_dollars ?? c.totalDollars;
    if (!out.costSummary.totalSpend?.trim()) {
      if (totalDollars != null && String(totalDollars).trim() !== '') {
        out.costSummary.totalSpend = `$${Number(totalDollars).toFixed(2)}`;
      } else if (totalCents != null && !Number.isNaN(Number(totalCents))) {
        out.costSummary.totalSpend = `$${(Number(totalCents) / 100).toFixed(2)}`;
      }
    }
    if (!out.costSummary.avgSessionCost?.trim() && perSessionCents != null && !Number.isNaN(Number(perSessionCents))) {
      out.costSummary.avgSessionCost = `$${(Number(perSessionCents) / 100).toFixed(2)}`;
    }
    const perOutcomeCents = c.per_outcome_cents ?? c.perOutcomeCents;
    if (!out.costSummary.avgOutcomeCost?.trim() && perOutcomeCents != null && !Number.isNaN(Number(perOutcomeCents))) {
      out.costSummary.avgOutcomeCost = `$${(Number(perOutcomeCents) / 100).toFixed(2)}`;
    }
  }

  let campSrc = d.campaign_rows ?? d.campaignRows;
  if (!Array.isArray(campSrc) && d.campaigns != null) {
    if (Array.isArray(d.campaigns)) campSrc = d.campaigns;
    else if (typeof d.campaigns === 'object' && Array.isArray(d.campaigns.campaigns)) campSrc = d.campaigns.campaigns;
  }
  if (Array.isArray(campSrc)) {
    out.campaignRows = campSrc.map((c: any) => {
      const sess = c.sessions ?? c.session_count ?? c.total_sessions ?? 0;
      const comp = c.completed ?? c.completed_sessions ?? 0;
      const compRate = c.completed_rate ?? c.completion_rate ?? (sess > 0 ? (comp / sess) * 100 : 0);
      const booked = c.booked ?? c.total_booked ?? c.appointments ?? c.total_appointments ?? 0;
      const rate = c.rate ?? c.book_rate ?? c.booking_rate ?? (sess > 0 ? (booked / sess) * 100 : 0);

      return {
        id: String(c.campaign_id ?? c.id ?? ''),
        name: String(c.name ?? ''),
        icon: String(c.icon ?? ''),
        status: String(c.status ?? (sess > 0 ? 'Live' : '')),
        scans: fmtAnalyticsCount(c.scans ?? c.scan_count ?? c.total_scans),
        sessions: fmtAnalyticsCount(sess),
        completed: String(comp),
        completedRate: `${Number(compRate).toFixed(0)}%`,
        booked: String(booked),
        rate: `${Number(rate).toFixed(0)}%`,
        avgDuration: fmtDurationSeconds(c.avg_duration_seconds ?? c.avg_duration),
        avgSentiment: c.avg_sentiment != null ? Number(c.avg_sentiment).toFixed(2) : (c.sentiment ?? '—'),
        rev: String(c.rev ?? c.revenue ?? ''),
        cpa: String(c.cpa ?? c.cost_per_appt ?? ''),
        color: (c.color as CallAnalyticsCampaignTableRow['color']) || 'success.main',
      };
    });
  }

  const agentTabSrc = d.agent_tab_kpis ?? d.agentTabKpis;
  if (Array.isArray(agentTabSrc)) out.agentTabKpis = agentTabSrc.map(mapCallAnalyticsKpiStat);

  const agentRowSrc = d.agent_rows ?? d.agentRows;
  if (Array.isArray(agentRowSrc)) {
    out.agentRows = agentRowSrc.map((a: any) => ({
      name: String(a.name ?? ''),
      icon: String(a.icon ?? ''),
      sessions: fmtAnalyticsCount(a.sessions),
      rate: String(a.rate ?? ''),
      sent: String(a.sent ?? a.sentiment ?? ''),
      esc: String(a.esc ?? a.escalation ?? ''),
      cpa: String(a.cpa ?? ''),
    }));
  }

  const costTabSrc = d.cost_tab_kpis ?? d.costTabKpis;
  if (Array.isArray(costTabSrc)) out.costTabKpis = costTabSrc.map(mapCallAnalyticsKpiStat);

  const costRowsSrc = d.cost_rows ?? d.costRows;
  if (Array.isArray(costRowsSrc)) out.costRows = costRowsSrc.map(mapCallAnalyticsCostRow);

  const pat = d.platform_adjusted_total ?? d.platformAdjustedTotal;
  if (pat != null && String(pat).trim() !== '') out.platformAdjustedTotal = String(pat);

  const ac = d.activity_chart ?? d.activityChart;
  if (ac && typeof ac === 'object') {
    const chart = ac as any;
    if (Array.isArray(chart.daily_labels)) out.activityChart.dailyLabels = chart.daily_labels.map(String);
    if (Array.isArray(chart.dailyLabels)) out.activityChart.dailyLabels = chart.dailyLabels.map(String);
    if (Array.isArray(chart.daily_totals)) out.activityChart.dailyTotals = chart.daily_totals.map(Number);
    if (Array.isArray(chart.dailyTotals)) out.activityChart.dailyTotals = chart.dailyTotals.map(Number);
    if (Array.isArray(chart.weekly_labels)) out.activityChart.weeklyLabels = chart.weekly_labels.map(String);
    if (Array.isArray(chart.weeklyLabels)) out.activityChart.weeklyLabels = chart.weeklyLabels.map(String);
    if (Array.isArray(chart.weekly_totals)) out.activityChart.weeklyTotals = chart.weekly_totals.map(Number);
    if (Array.isArray(chart.weeklyTotals)) out.activityChart.weeklyTotals = chart.weeklyTotals.map(Number);
    const seriesName = chart.series_name ?? chart.seriesName;
    if (seriesName != null && String(seriesName).trim() !== '') out.activityChart.seriesName = String(seriesName);
  }

  const trendsArr = d.trends?.trends ?? (Array.isArray(d.trends) ? d.trends : null);
  if (
    Array.isArray(trendsArr) &&
    trendsArr.length > 0 &&
    (!out.activityChart.dailyTotals.length || !out.activityChart.dailyLabels.length)
  ) {
    const sorted = [...trendsArr].sort((a: any, b: any) =>
      String(a.date ?? '').localeCompare(String(b.date ?? '')),
    );
    out.activityChart.dailyLabels = sorted.map((x: any) => {
      const raw = String(x.date ?? '');
      return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw.slice(5) : raw;
    });
    out.activityChart.dailyTotals = sorted.map((x: any) => Number(x.sessions ?? 0));
    if (!out.activityChart.seriesName?.trim()) out.activityChart.seriesName = 'Sessions';
  }

  const uiRaw = d.ui ?? d.labels;
  out.ui = coerceAnalyticsUi(uiRaw);

  if (overviewBlock) {
    if (overviewBlock.channels && typeof overviewBlock.channels === 'object') {
      out.channelData = Object.entries(overviewBlock.channels as Record<string, number>).map(([k, v]) => ({
        label: k.toUpperCase(),
        value: Number(v),
      }));
    }
    if (overviewBlock.outcomes && typeof overviewBlock.outcomes === 'object') {
      out.outcomeData = Object.entries(overviewBlock.outcomes as Record<string, number>).map(([k, v]) => ({
        label: k.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
        value: Number(v),
      }));
    }
  }

  return out;
}

export async function fetchCallAnalytics(
  axiosInstance?: AxiosInstance,
  params?: { start_date?: string; end_date?: string; campaign_id?: string; days?: number },
): Promise<CallAnalyticsPayload> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.analytics}/overview`;
  const response = await client.get<unknown>(url, params ? { params } : undefined);
  return normalizeCallAnalyticsFull(response.data);
}

export async function exportCallAnalytics(
  format: 'csv' | 'pdf' = 'csv',
  axiosInstance?: AxiosInstance,
  params?: { start_date?: string; end_date?: string; campaign_id?: string; days?: number }
): Promise<Blob> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.analytics}/export/sessions`;
  const response = await client.get<Blob>(url, { responseType: 'blob', params });
  return response.data;
}
